import React, { useCallback, useEffect, useState } from 'react'
import './AiReceptionist.css'
import {
  Accordion,
  AccordionItem,
  ToggleSwitch,
  InputField,
  Select,
  MultiselectCombobox,
  Breadcrumbs,
  Button,
  Modal,
  RichTextEditor,
  type FilterSelection,
  Checkbox,
} from '@enamelai/design-system'
import { Icon as Delete2Icon } from '@enamelai/design-system/icons/Delete2'
import AddPatientModal from '@components/layout/Modals/AddPatientModal/AddPatientModal.component'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import useCommunicationsLeaveGuard from '../shared/useCommunicationsLeaveGuard'
import EmailSender from '@components/layout/Modals/EmailSender/EmailSender.component'
import {
  ScheduleAppointment,
  SelectContactModal,
} from '@components/layout/Modals'
import moment from 'moment'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { getFullName } from '@utils/fullname.util'
import { useAIReceptionist } from './useAIReceptionist'
import {
  AiReceptionistFieldErrors,
  IAiReceptionistSettings,
} from '@dataTypes/ai.type'
import { getAiReceptionistFieldErrors } from '@utils/ai.util'
import AiReceptionistPatientsTab from './AiReceptionistPatientsTab.component'
import AiReceptionistAppointmentsTab from './AiReceptionistAppointmentsTab.component'
import AiReceptionistCallsTab from './AiReceptionistCallsTab.component'
import { LANGUAGES, VOICES } from '@constants/index'
import { AI_RECEPTIONIST_SMS_VARIABLES } from '@constants/ai.constant'
import AiReceptionistKnowledge, {
  knowledgeTopicSummary,
} from './AiReceptionistKnowledge.component'

const CONTROLS = [
  { value: 'new_booking', label: 'New booking' },
  { value: 'reschedule', label: 'Reschedule' },
  { value: 'cancel', label: 'Cancel' },
  { value: 'faq', label: 'FAQ' },
]

type SmsTemplateKey =
  | 'sms_booking_unconfirmed'
  | 'sms_booking_confirmed'
  | 'sms_reschedule'
  | 'sms_cancel'

const CONTROL_SMS_FIELDS: Partial<
  Record<string, Array<{ key: SmsTemplateKey; label: string }>>
> = {
  new_booking: [
    { key: 'sms_booking_unconfirmed', label: 'Appointment requested' },
    { key: 'sms_booking_confirmed', label: 'Appointment confirmed' },
  ],
  reschedule: [{ key: 'sms_reschedule', label: 'Reschedule' }],
  cancel: [{ key: 'sms_cancel', label: 'Cancellation' }],
}

type AllowedActionsAccordionProps = {
  settings: IAiReceptionistSettings
  fieldErrors: AiReceptionistFieldErrors
  onChange: (next: IAiReceptionistSettings) => void
}

const AllowedActionsAccordion = ({
  settings,
  fieldErrors,
  onChange,
}: AllowedActionsAccordionProps): React.ReactElement => {
  const selected = settings.controls ?? []

  const setControl = (value: string, checked: boolean): void => {
    const nextControls = checked
      ? [...selected, value]
      : selected.filter((item) => item !== value)
    onChange({ ...settings, controls: nextControls })
  }

  return (
    <div className="ai-receptionist-actions">
      <span className="ds-input-field__label">Allowed actions</span>
      {fieldErrors.controls ? (
        <p className="ai-receptionist-field-error">{fieldErrors.controls}</p>
      ) : null}
      <Accordion className="ai-receptionist-controls" defaultOpenIds={[]}>
        {CONTROLS.map((control) => {
          const enabled = selected.includes(control.value)
          const fields = CONTROL_SMS_FIELDS[control.value] ?? []
          return (
            <AccordionItem
              key={control.value}
              id={control.value}
              title={control.label}
              leading={
                <ToggleSwitch
                  checked={enabled}
                  aria-label={`Allow ${control.label}`}
                  disabled={!settings.enabled}
                  onChange={(checked) => setControl(control.value, checked)}
                />
              }
            >
              {enabled && fields.length > 0 ? (
                <div className="ai-receptionist-control__editors">
                  {fields.map((field) => (
                    <div
                      key={field.key}
                      className="ai-receptionist-control__editor"
                    >
                      <span className="ds-input-field__label">
                        {field.label}
                      </span>
                      <RichTextEditor
                        variant="sms-notes"
                        variables={AI_RECEPTIONIST_SMS_VARIABLES}
                        value={settings[field.key]}
                        placeholder="Type your message here"
                        bodyActions={[]}
                        footerActions={[]}
                        aiEnabled={false}
                        disabled={!settings.enabled}
                        onChange={(next) =>
                          onChange({ ...settings, [field.key]: next })
                        }
                      />
                      {fieldErrors[field.key] ? (
                        <p className="ai-receptionist-field-error">
                          {fieldErrors[field.key]}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </AccordionItem>
          )
        })}
      </Accordion>
    </div>
  )
}

const VERIFICATION = [
  { value: 'name', label: 'Name' },
  { value: 'date_of_birth', label: 'Date of birth' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
]

// TO DO: Move this to communications flow once design is finalized

const AiReceptionist = (): React.ReactElement => {
  const {
    activeTab,
    appointmentsLoading,
    appointmentToDelete,
    confirmDeleteAppointment,
    confirmDeletePatient,
    currentAppointment,
    editPatData,
    emailRecipient,
    filteredAppointments,
    filteredPatients,
    hasAppointmentSearch,
    hasChanges,
    handleCloseAddPatientModal,
    handleCloseEditAppointmentModal,
    handleCloseSelectContactModal,
    handleReset,
    handleSave,
    handleSaveAppointment,
    handleSavePatient,
    hasPatientSearch,
    loading,
    patientsLoading,
    settingsReady,
    patientToDelete,
    playPreview,
    runAppointmentAction,
    handleUpdateAppointmentStatus,
    runPatientAction,
    selectedContact,
    serviceOptions,
    setEmailRecipient,
    setSettings,
    setShowConfirmDeleteAppointment,
    setShowConfirmDeleteModal,
    settings,
    showAddPatientModal,
    showConfirmDeleteAppointment,
    showConfirmDeleteModal,
    showEditAppointmentModal,
    showSelectContactModal,
  } = useAIReceptionist()
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const leaveGuard = useCommunicationsLeaveGuard(
    hasChanges && activeTab === 'configurations' && settingsReady,
  )
  const [showFieldErrors, setShowFieldErrors] = useState(false)
  const [callSearchQuery, setCallSearchQuery] = useState('')
  const [callSearchFilters, setCallSearchFilters] = useState<FilterSelection>(
    {},
  )

  const [showTabSelection, setShowTabSelection] = useState<boolean>(false)
  const [showKnowledge, setShowKnowledge] = useState(false)
  const knowledgeSummary = knowledgeTopicSummary(settings)

  const fieldErrors = showFieldErrors
    ? getAiReceptionistFieldErrors(settings)
    : {}

  const handleCancel = useCallback(() => {
    leaveGuard.requestLeave(() => {
      setShowFieldErrors(false)
      handleReset()
    })
  }, [handleReset, leaveGuard.requestLeave])

  const handleSaveClick = useCallback(() => {
    const errors = getAiReceptionistFieldErrors(settings)
    if (Object.keys(errors).length > 0) {
      setShowFieldErrors(true)
      return
    }
    setShowFieldErrors(false)
    void handleSave()
  }, [handleSave, settings])

  useEffect(() => {
    setHeaderTitle(
      showKnowledge ? (
        <Breadcrumbs
          items={[
            {
              label: 'AI receptionist',
              onClick: () => setShowKnowledge(false),
            },
            { label: 'System prompt & FAQ' },
          ]}
          showBackIcon
          onBackClick={() => setShowKnowledge(false)}
        />
      ) : (
        'AI receptionist'
      ),
    )
    setHeaderButtons(
      activeTab === 'configurations' ? (
        <>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={!hasChanges || loading || !settingsReady}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="button"
            disabled={!hasChanges || loading || !settingsReady}
            onClick={handleSaveClick}
          >
            Save changes
          </Button>
        </>
      ) : null,
    )
    return () => {
      setHeaderTitle(null)
      setHeaderButtons(null)
    }
  }, [
    activeTab,
    hasChanges,
    handleCancel,
    handleSaveClick,
    loading,
    settingsReady,
    setHeaderButtons,
    setHeaderTitle,
    showKnowledge,
    showTabSelection,
  ])

  return (
    <div className="ai-receptionist-settings" id="settings-ai-receptionist">
      {leaveGuard.modal}
      {/*  <div className="ai-receptionist-tab-row">
        TODO: Move these to designated feature once design is finalized
         <Tabs
          orientation="horizontal"
          items={TAB_ITEMS}
          value={activeTab}
          onChange={(value) => {
            if (value === activeTab) return
            if (activeTab === 'configurations') {
              leaveGuard.requestLeave(() => {
                handleReset()
                setActiveTab(value)
              })
              return
            }
            setActiveTab(value)
          }}
        /> 
        {activeTab === 'patients' ? (
          <SearchWithFilter
            className="ai-receptionist-patients__search"
            placeholder="Search patients..."
            value={patientSearchQuery}
            onChange={setPatientSearchQuery}
            filters={patientStatusFilters}
            filterLabels={{ status: 'Status' }}
            selectedFilters={patientSearchFilters}
            onFilterChange={setPatientSearchFilters}
            showResetButton
            onReset={clearPatientSearch}
          />
        ) : null}
        {activeTab === 'appointments' ? (
          <SearchWithFilter
            className="ai-receptionist-appointments__search"
            placeholder="Search appointments..."
            value={appointmentSearchQuery}
            onChange={setAppointmentSearchQuery}
            filters={appointmentFilterOptions}
            filterLabels={{
              status: 'Status',
              provider: 'Provider',
              subcategory: 'Service',
            }}
            selectedFilters={appointmentSearchFilters}
            onFilterChange={setAppointmentSearchFilters}
            showResetButton
            onReset={clearAppointmentSearch}
          />
        ) : null}
        {activeTab === 'calls' ? (
          <SearchWithFilter
            className="ai-receptionist-calls__search"
            placeholder="Search calls..."
            value={callSearchQuery}
            onChange={setCallSearchQuery}
            filters={{
              type: ['Incoming', 'Outgoing', 'Missed', 'Decline'],
              sentiment: ['Positive', 'Negative', 'Neutral'],
            }}
            filterLabels={{ type: 'Call type', sentiment: 'Sentiment' }}
            selectedFilters={callSearchFilters}
            onFilterChange={setCallSearchFilters}
            showResetButton
            onReset={() => {
              setCallSearchQuery('')
              setCallSearchFilters({})
            }}
          />
        ) : null}
      </div>*/}

      {activeTab === 'configurations' && !settingsReady ? (
        <SettingsSkeleton variant="form" />
      ) : null}

      {activeTab === 'configurations' && settingsReady && showKnowledge ? (
        <AiReceptionistKnowledge
          settings={settings}
          disabled={!settings.enabled}
          onChange={setSettings}
        />
      ) : null}

      {activeTab === 'configurations' && settingsReady && !showKnowledge ? (
        <>
          <label className="ai-receptionist-enable-row">
            <ToggleSwitch
              checked={settings.enabled}
              aria-label="Enable AI receptionist"
              onChange={(checked) =>
                setSettings({ ...settings, enabled: checked })
              }
            />
            Enable AI receptionist
          </label>
          <div className="d-flex gap-3">
            <InputField
              fullWidth
              variant="text"
              label="Name"
              optional
              value={settings.name}
              onChange={(event) =>
                setSettings({ ...settings, name: event.target.value })
              }
              onClear={() => setSettings({ ...settings, name: '' })}
              disabled={!settings.enabled}
            />
            <div className="d-flex w-100" />
          </div>
          <div className="d-flex gap-3">
            <Select
              label="Receptionist voice"
              value={settings.voice_style}
              options={VOICES.map((voice) => ({
                value: voice,
                label: voice,
              }))}
              onChange={(value) =>
                setSettings({
                  ...settings,
                  voice_style: value as string,
                })
              }
              disabled={!settings.enabled}
              error={Boolean(fieldErrors.voice_style)}
              helperText={fieldErrors.voice_style}
              trailingAction={{
                label: 'Preview',
                onClick: () => playPreview('voice_style'),
              }}
            />
            <MultiselectCombobox
              label="Supported languages"
              placeholder="Search language"
              value={settings.languages}
              options={LANGUAGES.map((language) => ({
                value: language,
                label: language,
              }))}
              onChange={(value) =>
                setSettings({ ...settings, languages: value as string[] })
              }
              disabled={!settings.enabled}
              error={Boolean(fieldErrors.languages)}
              helperText={fieldErrors.languages}
            />
          </div>
          <div className="ai-receptionist-channel-group">
            <span className="ds-input-field__label">
              Communication channels
            </span>
            <div className="d-flex gap-3">
              <Checkbox
                label="Call"
                checked={settings.channels?.includes('call')}
                disabled={!settings.enabled}
                onChange={(e) => {
                  const checked = e.target.checked
                  setSettings({
                    ...settings,
                    channels: checked
                      ? [...(settings.channels ?? []), 'call']
                      : (settings.channels ?? []).filter(
                          (channel) => channel !== 'call',
                        ),
                  })
                }}
              />
              <Checkbox
                label="SMS"
                checked={settings.channels?.includes('sms')}
                disabled={!settings.enabled}
                onChange={(e) => {
                  const checked = e.target.checked
                  setSettings({
                    ...settings,
                    channels: checked
                      ? [...(settings.channels ?? []), 'sms']
                      : (settings.channels ?? []).filter(
                          (channel) => channel !== 'sms',
                        ),
                  })
                }}
              />
            </div>
          </div>
          <MultiselectCombobox
            label="Services"
            placeholder="Search services"
            value={settings.services}
            options={serviceOptions}
            onChange={(value) =>
              setSettings({ ...settings, services: value as string[] })
            }
            disabled={!settings.enabled}
            error={Boolean(fieldErrors.services)}
            helperText={fieldErrors.services}
            maxVisibleSelected={4}
          />
          <label className="ai-receptionist-enable-row">
            <ToggleSwitch
              checked={settings.verification_enabled !== false}
              aria-label="Enable patient verification"
              disabled={!settings.enabled}
              onChange={(checked) =>
                setSettings({ ...settings, verification_enabled: checked })
              }
            />
            Patient verification
          </label>
          <div className="ai-receptionist-channel-group">
            <span className="ds-input-field__label">Verification methods</span>
            <div className="d-flex gap-3">
              {VERIFICATION.map(({ value, label }) => (
                <Checkbox
                  key={value}
                  disabled={
                    !settings.enabled || settings.verification_enabled === false
                  }
                  label={label}
                  checked={settings.verification_fields?.includes(value)}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setSettings({
                      ...settings,
                      verification_fields: checked
                        ? [...(settings.verification_fields ?? []), value]
                        : (settings.verification_fields ?? []).filter(
                            (f) => f !== value,
                          ),
                    })
                  }}
                />
              ))}
            </div>
            {fieldErrors.verification_fields && (
              <span
                role="alert"
                className="ds-multiselect-combobox__helper--error ds-multiselect-combobox__helper"
              >
                {fieldErrors.verification_fields}
              </span>
            )}
          </div>
          <MultiselectCombobox
            label="Verification required for"
            placeholder="Select actions requiring verification"
            value={settings.verification_required_for}
            options={CONTROLS.filter(({ value }) => value !== 'callback')}
            onChange={(value) =>
              setSettings({
                ...settings,
                verification_required_for: value as string[],
              })
            }
            disabled={
              !settings.enabled || settings.verification_enabled === false
            }
            error={Boolean(fieldErrors.verification_required_for)}
            helperText={fieldErrors.verification_required_for}
          />
          <AllowedActionsAccordion
            settings={settings}
            fieldErrors={fieldErrors}
            onChange={setSettings}
          />
          <div className="d-flex justify-content-between">
            <label className="ai-receptionist-enable-row">
              System prompt & FAQ
            </label>
            <Button
              variant="primary"
              size="sm"
              type="button"
              onClick={() => setShowKnowledge(true)}
            >
              {knowledgeSummary.action}
            </Button>
          </div>
        </>
      ) : // TO DO: Move this to communications flow once design is finalized
      // {(settings.channels ?? []).includes('call') ? (
      //   <>
      //     <Select
      //       label="Voice tone"
      //       placeholder="Select voice tone"
      //       value={settings.voice_tone}
      //       options={TONES.map((tone) => ({
      //         value: tone.value,
      //         label: tone.label,
      //       }))}
      //       onChange={(value) =>
      //         setSettings({
      //           ...settings,
      //           voice_tone: value as AiVoiceTone,
      //         })
      //       }
      //       disabled={!settings.enabled}
      //       error={Boolean(fieldErrors.voice_tone)}
      //       helperText={fieldErrors.voice_tone}
      //       trailingAction={{
      //         label: 'Preview',
      //         onClick: () => playPreview('voice_tone'),
      //         // disabled: previewing || !settings.enabled,
      //       }}
      //     />

      //     <div className="ai-receptionist-sms-fields">
      //       <InputField
      //         variant="message"
      //         rows={3}
      //         label="New booking"
      //         value={settings.sms_booking_unconfirmed}
      //         onChange={(event) =>
      //           setSettings({
      //             ...settings,
      //             sms_booking_unconfirmed: event.target.value,
      //           })
      //         }
      //         clearable={false}
      //         showCounter={false}
      //         disabled={!settings.enabled}
      //         error={Boolean(fieldErrors.sms_booking_unconfirmed)}
      //         helperText={fieldErrors.sms_booking_unconfirmed}
      //       />
      //       <InputField
      //         variant="message"
      //         rows={3}
      //         label="Confirmed appointment"
      //         value={settings.sms_booking_confirmed}
      //         onChange={(event) =>
      //           setSettings({
      //             ...settings,
      //             sms_booking_confirmed: event.target.value,
      //           })
      //         }
      //         clearable={false}
      //         showCounter={false}
      //         disabled={!settings.enabled}
      //         error={Boolean(fieldErrors.sms_booking_confirmed)}
      //         helperText={fieldErrors.sms_booking_confirmed}
      //       />
      //       <InputField
      //         variant="message"
      //         rows={3}
      //         label="Reschedule"
      //         value={settings.sms_reschedule}
      //         onChange={(event) =>
      //           setSettings({
      //             ...settings,
      //             sms_reschedule: event.target.value,
      //           })
      //         }
      //         clearable={false}
      //         showCounter={false}
      //         disabled={!settings.enabled}
      //         error={Boolean(fieldErrors.sms_reschedule)}
      //         helperText={fieldErrors.sms_reschedule}
      //       />
      //       <InputField
      //         variant="message"
      //         rows={3}
      //         label="Cancellation"
      //         value={settings.sms_cancel}
      //         onChange={(event) =>
      //           setSettings({
      //             ...settings,
      //             sms_cancel: event.target.value,
      //           })
      //         }
      //         clearable={false}
      //         showCounter={false}
      //         disabled={!settings.enabled}
      //         error={Boolean(fieldErrors.sms_cancel)}
      //         helperText={fieldErrors.sms_cancel}
      //       />
      //     </div>
      //   </>
      // ) : null}
      // {(settings.channels ?? []).includes('sms') ? (
      //   <SectionCard
      //     title="SMS assistant"
      //     headerAfterTitle={
      //       <span
      //         className="ds-section-card__icon-button"
      //         data-bs-toggle="tooltip"
      //         data-bs-placement="top"
      //         title="Applies to SMS AI only."
      //       >
      //         <InfoIcon aria-hidden="true" />
      //       </span>
      //     }
      //   >
      //     <div className="row-3-2">
      //       <InputWithUnit
      //         label="Default response time"
      //         helperText={
      //           fieldErrors.sms_default_response_value ??
      //           'If there is no response from the team within this duration, the AI will assist the patient.'
      //         }
      //         error={Boolean(fieldErrors.sms_default_response_value)}
      //         value={String(settings.sms_default_response_value ?? 15)}
      //         unit={settings.sms_default_response_unit ?? 'minutes'}
      //         units={[
      //           { value: 'minutes', label: 'minutes' },
      //           { value: 'hours', label: 'hours' },
      //         ]}
      //         onChange={(value) => {
      //           const parsed = Number(value)
      //           setSettings({
      //             ...settings,
      //             sms_default_response_value: Number.isFinite(parsed)
      //               ? Math.max(1, Math.floor(parsed))
      //               : 15,
      //           })
      //         }}
      //         onUnitChange={(unit) =>
      //           setSettings({
      //             ...settings,
      //             sms_default_response_unit:
      //               unit === 'hours' ? 'hours' : 'minutes',
      //           })
      //         }
      //         disabled={!settings.enabled}
      //       />
      //       <label className="ai-receptionist-sms-toggle">
      //         <span className="ds-type-paragraph-small">
      //           Answer inquiry during lunch break
      //         </span>
      //         <ToggleSwitch
      //           checked={settings.sms_answer_during_lunch !== false}
      //           aria-label="Answer inquiry during lunch break"
      //           onChange={(checked) =>
      //             setSettings({
      //               ...settings,
      //               sms_answer_during_lunch: checked,
      //             })
      //           }
      //           disabled={!settings.enabled}
      //         />
      //       </label>
      //       <label className="ai-receptionist-sms-toggle">
      //         <span className="ds-type-paragraph-small">
      //           Answer inquiry when closed
      //         </span>
      //         <ToggleSwitch
      //           checked={settings.sms_answer_when_closed !== false}
      //           aria-label="Answer inquiry when closed"
      //           onChange={(checked) =>
      //             setSettings({
      //               ...settings,
      //               sms_answer_when_closed: checked,
      //             })
      //           }
      //           disabled={!settings.enabled}
      //         />
      //       </label>
      //     </div>
      //   </SectionCard>
      // ) : null}
      null}

      {activeTab === 'patients' ? (
        <AiReceptionistPatientsTab
          loading={patientsLoading}
          patients={filteredPatients}
          emptyDescription={
            !patientsLoading &&
            hasPatientSearch &&
            filteredPatients.length === 0
              ? 'No patients match your search.'
              : undefined
          }
          onView={(patientId) => {
            void runPatientAction(patientId, 'view')
          }}
          onEdit={(patientId) => {
            void runPatientAction(patientId, 'edit')
          }}
          onDelete={(patientId) => {
            void runPatientAction(patientId, 'delete')
          }}
          onPhone={(patientId) => {
            void runPatientAction(patientId, 'phone')
          }}
          onMail={(patientId) => {
            void runPatientAction(patientId, 'mail')
          }}
          onMessage={(patientId) => {
            void runPatientAction(patientId, 'message')
          }}
        />
      ) : null}

      {activeTab === 'appointments' ? (
        <AiReceptionistAppointmentsTab
          loading={appointmentsLoading}
          appointments={filteredAppointments}
          emptyDescription={
            !appointmentsLoading &&
            hasAppointmentSearch &&
            filteredAppointments.length === 0
              ? 'No appointments match your search.'
              : undefined
          }
          onView={(appointment) => {
            void runAppointmentAction(appointment, 'view')
          }}
          onEdit={(appointment) => {
            void runAppointmentAction(appointment, 'edit')
          }}
          onDelete={(appointment) => {
            void runAppointmentAction(appointment, 'delete')
          }}
          onStatusChange={(appointment, status) => {
            void handleUpdateAppointmentStatus(appointment, status)
          }}
        />
      ) : null}

      {activeTab === 'calls' ? (
        <AiReceptionistCallsTab
          searchQuery={callSearchQuery}
          selectedFilters={callSearchFilters}
        />
      ) : null}

      {showAddPatientModal ? (
        <AddPatientModal
          isOpen={showAddPatientModal}
          onRequestClose={handleCloseAddPatientModal}
          onSave={handleSavePatient}
          patientData={editPatData ?? undefined}
        />
      ) : null}
      <Modal
        className="patient-delete-modal"
        open={showConfirmDeleteModal}
        onClose={() => setShowConfirmDeleteModal(false)}
        closeOnBackdrop={false}
        size="sm"
        variant="confirmation"
        title="Delete patient"
        icon={<Delete2Icon width={16} height={16} aria-hidden="true" />}
        iconTone="danger"
        description={`Are you sure you want to delete ${
          patientToDelete ? getFullName(patientToDelete) : 'this patient'
        }?`}
        primaryLabel="Delete"
        secondaryLabel="Cancel"
        primaryDestructive
        onPrimaryClick={() => {
          void confirmDeletePatient()
        }}
        onSecondaryClick={() => setShowConfirmDeleteModal(false)}
      />
      {showSelectContactModal && selectedContact ? (
        <SelectContactModal
          show={showSelectContactModal}
          onHide={handleCloseSelectContactModal}
          contactData={selectedContact}
        />
      ) : null}
      <EmailSender
        show={emailRecipient !== null}
        onHide={() => setEmailRecipient(null)}
        recipientEmail={emailRecipient ?? ''}
      />
      {showEditAppointmentModal && currentAppointment ? (
        <ScheduleAppointment
          isOpen={showEditAppointmentModal}
          handleModal={handleCloseEditAppointmentModal}
          onSave={handleSaveAppointment}
          initialStartTime={moment(currentAppointment.appt_date).format(
            'YYYY-MM-DD',
          )}
          initialEndTime={moment(currentAppointment.appt_date).format(
            'YYYY-MM-DD',
          )}
          initialRoom={currentAppointment.operatory_id}
          initialProvider={currentAppointment.provider_id}
          currentAppt={currentAppointment}
        />
      ) : null}
      <Modal
        open={showConfirmDeleteAppointment}
        onClose={() => setShowConfirmDeleteAppointment(false)}
        closeOnBackdrop={false}
        size="sm"
        variant="confirmation"
        title="Delete appointment"
        icon={<Delete2Icon width={16} height={16} aria-hidden="true" />}
        iconTone="danger"
        description={`Are you sure you want to delete ${
          appointmentToDelete
            ? `${appointmentToDelete.patient_name}'s appointment`
            : 'this appointment'
        }?`}
        primaryLabel="Delete"
        secondaryLabel="Cancel"
        primaryDestructive
        onPrimaryClick={() => {
          void confirmDeleteAppointment()
        }}
        onSecondaryClick={() => setShowConfirmDeleteAppointment(false)}
      />
    </div>
  )
}

export default AiReceptionist
